// Helpers/GetCashflowClientsForAdviser.js
import { doc, getDoc } from "firebase/firestore";
import { getAllCashflowEntries } from "../FirebaseHelpers/FirebaseHelpers"; // adjust import!
import { firestore as db } from "../../../../backend/firebaseconfig";
import dayjs from "dayjs";

/**
 * Fetch Cashflow Modeller clients for a single adviser.
 * Includes:
 *   - Adviser's own clients
 *   - + any linked admins' clients (with role=Administrators)
 *
 * @param {string} role - User's role ("Advisers")
 * @param {string} email - User's email
 * @returns {Promise<Array>} Array of enriched client objects
 */

export const getCashflowClientsForAdviser = async (role, email) => {
  if (!role || !email) return [];
  const allRows = [];

  // --- Adviser's own entries
  const normBool = (v) => v === true || v === "true" || v === 1 || v === "1";
 
  const adviserEntries = await getAllCashflowEntries(role, email);

  const adviserRef = doc(db, "Authorized Members", role, "Data", email);
  const adviserSnap = await getDoc(adviserRef);
  const adviserData = adviserSnap.exists() ? adviserSnap.data() : {};

  const adviserRows = adviserEntries.map((entry) => ({
    id: entry.uid,
    role: "Advisers",
    name:
      entry.details && Array.isArray(entry.details)
        ? entry.details
            .slice(0, 2)
            .map((d) => d.name)
            .filter(Boolean)
            .join(" & ") || "Unnamed Client"
        : "Unnamed Client",
    rawDate: entry.createdAt || null,
    date: entry.createdAt
      ? dayjs(entry.createdAt).format("DD/MM/YYYY")
      : "Unknown",
    createdBy: email,
    updatedOn: entry.updatedOn
      ? dayjs(entry.updatedOn).format("DD/MM/YYYY")
      : "",
    tradingName:
      adviserData.tradingName || "Rosemount Financial Solutions (IFA) Ltd",
    primaryColor: adviserData.primaryColor || "081e56",
    isScenario: normBool(entry?.isScenario),
    parentDocId: entry?.parentDocId || "",
    scenarios: Array.isArray(entry?.scenarios)
      ? entry.scenarios
          .map((s) => ({ id: s?.id, name: s?.name }))
          .filter((s) => !!s.id)
      : [],
  }));
  allRows.push(...adviserRows);

  // --- Admin entries (if adviser has admins[])
  const adminEmails = Array.isArray(adviserData.admins)
    ? adviserData.admins
    : [];

  for (const adminEmail of adminEmails) {
    const adminEntries = await getAllCashflowEntries(
      "Administrators",
      adminEmail
    );
    if (!adminEntries || adminEntries.length === 0) continue;

    const adminRef = doc(
      db,
      "Authorized Members",
      "Administrators",
      "Data",
      adminEmail
    );
    const adminSnap = await getDoc(adminRef);
    const adminData = adminSnap.exists() ? adminSnap.data() : {};

    const adminRows = adminEntries
      .filter((entry) => entry.forAdviser === email)
      .map((entry) => ({
        id: entry.uid,
        role: "Administrators",
        name:
          entry.details && Array.isArray(entry.details)
            ? entry.details
                .slice(0, 2)
                .map((d) => d.name)
                .filter(Boolean)
                .join(" & ") || "Unnamed Client"
            : "Unnamed Client",
        rawDate: entry.createdAt || null,
        date: entry.createdAt
          ? dayjs(entry.createdAt).format("DD/MM/YYYY")
          : "Unknown",
        createdBy: adminEmail,
        updatedOn: entry.updatedOn
          ? dayjs(entry.updatedOn).format("DD/MM/YYYY")
          : "",
        tradingName:
          adminData.tradingName || "Rosemount Financial Solutions (IFA) Ltd",
        primaryColor: adminData.primaryColor || "081e56",
        isScenario: normBool(entry?.isScenario),
        parentDocId: entry?.parentDocId || "",
        scenarios: Array.isArray(entry?.scenarios)
          ? entry.scenarios
              .map((s) => ({ id: s?.id, name: s?.name }))
              .filter((s) => !!s.id)
          : [],
      }));

    allRows.push(...adminRows);
  }

  // --- Sort newest first
  return allRows.sort((a, b) => {
    const A = a.rawDate ? new Date(a.rawDate).getTime() : 0;
    const B = b.rawDate ? new Date(b.rawDate).getTime() : 0;
    return B - A;
  });
};
